import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ActionButton, Content, Dialog, DialogContainer, DialogTrigger, Item, Menu, MenuTrigger } from '@adobe/react-spectrum';
import { Key } from '@react-types/shared';
import PrintDialog from './component/PrintDialog/PrintDialog';
import { FPAData } from '../../../../infra/protected/FPA/FPAData';

interface IButtonsComponentProps {
  styles: any;
  AtollonIcon: any;
  PrintIcon: any;
  EditIcon: any;
  ToolIcon: any;
  TrashIcon: any;
  onPrint?: (item: FPAData) => void;
  onEdit?: (item: FPAData) => void;
  onDelete?: (item: FPAData) => void;
  onTool?: (item: FPAData) => void;
  onAI?: (item: FPAData) => void;
  onCopy?: (item: FPAData) => void;
  onMove?: (item: FPAData) => void;
  selectedItem?: any;
  onCopyLink?:(item:FPAData) => void;
  extraMenuItems?: Array<{ key: string; name: string; onAction: () => void }>;
}

const ButtonsComponent: React.FC<IButtonsComponentProps> = props => {
  const { styles, AtollonIcon, PrintIcon, EditIcon, ToolIcon, TrashIcon, onPrint, onEdit, onDelete, onTool, onAI, onCopy, onMove, selectedItem,onCopyLink, extraMenuItems } = props;
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [availableWidth, setAvailableWidth] = useState(0);
  const [isPrintOpen, setIsPrintOpen] = useState(false);

  useEffect(() => {
    const node = rootRef.current;
    if (!node || typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      setAvailableWidth(entry?.contentRect?.width || 0);
    });

    observer.observe(node);
    setAvailableWidth(node.getBoundingClientRect().width);

    return () => observer.disconnect();
  }, []);

  const handlePrintClicked = useCallback(() => {
    if (onPrint) {
      onPrint(selectedItem);
    }
  }, [onPrint, selectedItem]);

  const handleEditClicked = useCallback(() => {
    console.log('handleEditClicked');
    if (onEdit) {
      onEdit(selectedItem);
    }
  }, [onEdit, selectedItem]);

  const handleDeleteClicked = useCallback(() => {
    if (onDelete) {
      onDelete(selectedItem);
    }
  }, [onDelete, selectedItem]);

  const handleToolClicked = useCallback(() => {
    console.log('handleToolClicked');
    if (onTool) {
      onTool(selectedItem);
    }
  }, [onTool, selectedItem]);

  const handleAIClicked = useCallback(() => {
    console.log('handleAIClicked');
    if (onAI) {
      onAI(selectedItem);
    }
  }, [onAI, selectedItem]);

  const handleMenuClicked = (key: Key) => {
    if (key === 'ai') {
      handleAIClicked();
    } else if (key === 'print') {
      setIsPrintOpen(true);
      handlePrintClicked();
    } else if (key === 'edit') {
      handleEditClicked();
    } else if (key === 'tool') {
      handleToolClicked();
    } else if (key === 'delete') {
      handleDeleteClicked();
    } else if (key === 'copy') {
      if (onCopy) {
        onCopy(selectedItem);
      }
    } else if (key === 'move') {
      if (onMove) {
        onMove(selectedItem);
      }
    }else if(key === 'copyLink'){
      if(onCopyLink){
        onCopyLink(selectedItem);
      }
    } else if (extraMenuItems?.length) {
      const match = extraMenuItems.find((x) => x.key === String(key));
      if (match) {
        match.onAction();
      }
    }
  };

  const primaryActions = useMemo(
    () => [
      {
        key: 'print',
        name: 'Print',
        render: () => (
          <div className={styles.print_icon} onClick={handlePrintClicked}>
            <DialogTrigger type="popover">
              <ActionButton UNSAFE_style={{ border: 'none' }}>
                <PrintIcon size={32} />
              </ActionButton>
              <Dialog>
                <Content>
                  <PrintDialog selectedItem={selectedItem} print_type={selectedItem?.type} />
                </Content>
              </Dialog>
            </DialogTrigger>
          </div>
        ),
      },
      { key: 'edit', name: 'Edit', render: () => <div className={styles.edit_icon} onClick={handleEditClicked}><EditIcon size={32} /></div> },
      { key: 'delete', name: 'Delete', render: () => <div className={styles.delete_icon} onClick={handleDeleteClicked}><TrashIcon size={32} /></div> },
    ] as Array<{ key: string; name: string; render: () => React.ReactNode }>,
    [onPrint, onEdit, onDelete, selectedItem, PrintIcon, EditIcon, TrashIcon, handlePrintClicked, handleEditClicked, handleDeleteClicked, styles.print_icon, styles.edit_icon, styles.delete_icon]
  );

  const secondaryMenuItems = useMemo(
    () => [
      { key: 'ai', name: 'AI Assistant' },
      { key: 'tool', name: 'Settings' },
      onCopy && { key: 'copy', name: 'Copy' },
      onCopyLink && { key: 'copyLink', name: 'Copy Link' },
      onMove && { key: 'move', name: 'Move' },
    ].filter(Boolean) as Array<{ key: string; name: string }>,
    [onCopy, onCopyLink, onMove]
  );

  const visiblePrimaryCount = useMemo(() => {
    if (availableWidth >= 260) return Math.min(primaryActions.length, 3);
    if (availableWidth >= 180) return Math.min(primaryActions.length, 2);
    return Math.min(primaryActions.length, 1);
  }, [availableWidth, primaryActions.length]);

  const visiblePrimaryActions = primaryActions.slice(0, visiblePrimaryCount);
  const overflowPrimaryActions = primaryActions.slice(visiblePrimaryCount);
  const menuItems = [
    ...overflowPrimaryActions.map((item) => ({ key: item.key, name: item.name })),
    ...secondaryMenuItems,
    ...(extraMenuItems?.map((item) => ({ key: item.key, name: item.name })) ?? []),
  ];

  return (
    <div ref={rootRef} className={styles.icon_list_child}>
      {visiblePrimaryActions.map((action) => (
        <React.Fragment key={action.key}>{action.render()}</React.Fragment>
      ))}
      {menuItems.length > 0 && (
        <MenuTrigger>
          <ActionButton UNSAFE_className={styles.moreButton} UNSAFE_style={{ border: 'none' }}>...</ActionButton>
          <Menu items={menuItems} onAction={handleMenuClicked}>
            {(item:any) => <Item key={item.key}>{item.name}</Item>}
          </Menu>
        </MenuTrigger>
      )}
      <DialogContainer onDismiss={() => setIsPrintOpen(false)}>
        {isPrintOpen && (
          <Dialog>
            <Content>
              <PrintDialog selectedItem={selectedItem} print_type={selectedItem?.type} />
            </Content>
          </Dialog>
        )}
      </DialogContainer>
    </div>
  );
};

export default ButtonsComponent;
